import { AxiosResponse } from "axios";
import {
  CompleteProjectDto,
  ProjectBillingCheckmarksDto,
  ProjectDetailsDto,
  ProjectOverviewDto,
  ProjectUpdateDto,
  TenderedProjectDto,
  UpdatedProjectDataDto,
  CoreCompetencyDto,
  ApplicationSuggestionsDto,
  ProjectApplicationDto,
  ProjectBillingDetailsDto,
  ProjectBillingDocumentUpdateDto,
  ProjectMemberUpdateDto,
  MemberBTAllocationDto,
  ProjectTeamCompositionDto,
  ProjectApplicantDto,
  SecondApplicationPhaseDto,
} from "../types/projectTypes";
import api from "../utils/api";
import { MembersFieldDto } from "../types/membersTypes";

/**
 * Get all projects
 * @returns The projects
 */
export const getAllProjects = async (): Promise<AxiosResponse<ProjectOverviewDto[]>> => {
  return await api.get<ProjectOverviewDto[]>(`/projects`);
};

/**
 * Get all core competencies
 * @returns The core competencies of type CoreCompetencyDto[]
 */
export const getCoreCompetencies = async (): Promise<AxiosResponse<CoreCompetencyDto[]>> => {
  return await api.get<CoreCompetencyDto[]>(`/projects/core-competencies`);
};

/**
 * Get all projects with the status "Bewerbung"
 * @returns The projects with the status "Bewerbung"
 */
export const getTenderedProjects = async (): Promise<AxiosResponse<TenderedProjectDto[]>> => {
  return await api.get<TenderedProjectDto[]>(`/projects/tendered`);
};

/**
 * Get all projects with the billing checkmarks
 * @returns The projects with the billing checkmarks
 */
export const getBillingCheckmarks = async (): Promise<AxiosResponse<ProjectBillingCheckmarksDto[]>> => {
  return await api.get<ProjectBillingCheckmarksDto[]>(`/projects/billing-checkmarks`);
};

/**
 * Get a specific project by ID
 * @param projectId - The ID of the project
 * @returns The project of type ProjectDetailsDto
 */
export const getProjectById = async (projectId: number): Promise<AxiosResponse<ProjectDetailsDto>> => {
  return await api.get<ProjectDetailsDto>(`/projects/${projectId}`);
};

/**
 * Update a project
 * @param projectId - The ID of the project
 * @param requestBody - The request body
 * @returns The updated project
 */
export const updateProject = async (updatedProject: ProjectUpdateDto): Promise<AxiosResponse<void>> => {
  return await api.put<void>(`/projects/${updatedProject.projectId}`, updatedProject);
};

/**
 * Call off a project
 * @param projectId The id of the project to call off
 * @returns void
 */
export const callOffProject = async (projectId: number): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/update-status/call-off`);
};

/**
 * Change the status of the project to "Bewerbung"
 * @param projectId The id of the project to change the status for
 * @returns void
 */
export const startTendering = async (projectId: number): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/update-status/start-tendering`);
};

/**
 * Change the status of the project to "Durchführung"
 * @param projectId The id of the project to change the status for
 * @returns void
 */
export const offerAccepted = async (projectId: number): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/update-status/offer-accepted`);
};

/**
 * Change the status of the project to "AngebotAbgelehnt"
 * @param projectId The id of the project to change the status for
 * @returns void
 */
export const offerRejected = async (projectId: number): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/update-status/offer-rejected`);
};

/**
 * Change the status of the project to "PitchVerloren"
 * @param projectId The id of the project to change the status for
 * @returns void
 */
export const pitchLost = async (projectId: number): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/update-status/pitch-lost`);
};

/**
 * Change the status of the project to "Abrechnung"
 * @param projectId The id of the project to change the status for
 * @returns void
 */
export const startBilling = async (projectId: number): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/update-status/start-billing`);
};

/**
 * Complete the project (transition from "Abrechnung" to "Abgeschlossen"). Requires a
 * mandatory Projektabschlussnotiz with at least 30 characters.
 */
export const completeProject = async (
  projectId: number,
  requestBody: CompleteProjectDto
): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/complete`, requestBody);
};

/**
 * Update the editable Abschlussnotizen before the project is completed.
 */
export const updateProjectCompletionNote = async (
  projectId: number,
  projectCompletionNote: string
): Promise<AxiosResponse<void>> => {
  return await api.put(`/projects/${projectId}/completion-note`, { projectCompletionNote });
};

export const updateProjectClosureWaiver = async (
  projectId: number,
  projectClosureWaiverReason: string,
  plannedFollowUpActivity?: boolean
): Promise<AxiosResponse<void>> => {
  return await api.put(`/projects/${projectId}/closure-waiver`, {
    projectClosureWaiverReason,
    plannedFollowUpActivity,
  });
};

/**
 * Update the project data
 * @param projectId The id of the project to update
 * @param updatedProjectData The project data to update
 * @returns void
 */
export const updateProjectData = async (
  projectId: number,
  updatedProjectData: UpdatedProjectDataDto
): Promise<AxiosResponse<void>> => {
  return await api.put(`/projects/${projectId}/project-data`, updatedProjectData);
};

/**
 * Checks if the member can be a PL for the project
 * @param memberId The ID of the member to check
 * @returns A promise that resolves to true if the member can be a PL, false otherwise
 */
export const getCanPL = async (memberId: number): Promise<AxiosResponse<boolean>> => {
  return await api.get(`/projects/members/${memberId}/can-pl`);
};

/**
 * Checks if the member can be a QM for the project
 * @param memberId The ID of the member to check
 * @returns A promise that resolves to true if the member can be a QM, false otherwise
 */
export const getCanQM = async (memberId: number): Promise<AxiosResponse<boolean>> => {
  return await api.get(`/projects/members/${memberId}/can-qm`);
};

/**
 * Get the application suggestions for the member
 * @param memberId The ID of the member
 * @returns The application suggestions
 */
export const getApplicationSuggestions = async (
  memberId: number
): Promise<AxiosResponse<ApplicationSuggestionsDto>> => {
  return await api.get(`/projects/members/${memberId}/application-suggestions`);
};

/**
 * Get the application data of the user for the project
 * @param projectId The ID of the project
 * @returns The application
 */
export const getUserApplication = async (projectId: number): Promise<AxiosResponse<ProjectApplicationDto | null>> => {
  return await api.get(`/projects/${projectId}/applications/me`);
};

/**
 * Get the applications for the project
 * @param projectId The ID of the project
 * @returns The applications
 */
export const getProjectApplications = async (projectId: number): Promise<AxiosResponse<ProjectApplicantDto[]>> => {
  return await api.get(`/projects/${projectId}/applications`);
};

/**
 * Saves the application for the project
 * @param projectId The ID of the project
 * @param application The application to save
 * @returns A promise that resolves when the application is saved
 */
export const saveApplication = async (application: ProjectApplicationDto): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${application.projectId}/applications`, application);
};

/**
 * Deletes the application for the project
 * @param projectId The ID of the project
 * @returns A promise that resolves when the application is deleted
 */
export const deleteApplication = async (projectId: number): Promise<AxiosResponse<void>> => {
  return await api.delete(`/projects/${projectId}/applications/me`);
};

/**
 * Get the project billing details
 * @param projectId The ID of the project
 * @returns The project billing details
 */
export const getProjectBillingDetails = async (projectId: number): Promise<AxiosResponse<ProjectBillingDetailsDto>> => {
  return await api.get(`/projects/${projectId}/billing-details`);
};

/**
 * Updates the project billing details
 * @param projectId The ID of the project
 * @param billingDocument The billing document to update
 * @returns A promise that resolves when the project billing details are updated
 */
export const updateProjectBillingDetails = async (
  projectId: number,
  billingDocument: ProjectBillingDocumentUpdateDto
): Promise<AxiosResponse<void>> => {
  return await api.put(`/projects/${projectId}/billing-details`, billingDocument);
};

/**
 * Updates the project member billing details
 * @param projectId The ID of the project
 * @param memberId The ID of the member
 * @param billingDocument The billing document to update
 * @returns A promise that resolves when the project member billing details are updated
 */
export const updateProjectMemberBillingDetails = async (
  projectId: number,
  memberId: number,
  billingDocument: ProjectMemberUpdateDto
): Promise<AxiosResponse<void>> => {
  return await api.put(`/projects/${projectId}/billing-details/${memberId}`, billingDocument);
};

/**
 * Updates the BT allocation for the project
 * @param projectId The ID of the project
 * @param memberBTAllocation The BT allocation to update
 * @returns A promise that resolves when the BT allocation is updated
 */
export const updateBTAllocation = async (
  projectId: number,
  memberBTAllocation: MemberBTAllocationDto[]
): Promise<AxiosResponse<void>> => {
  return await api.put(`/projects/${projectId}/bt-allocation`, memberBTAllocation);
};

/**
 * Updates the staffing committee for the project
 * @param projectId The ID of the project
 * @param staffingCommittee The staffing committee to update
 * @returns A promise that resolves when the staffing committee is updated
 */
export const updateStaffingCommittee = async (
  projectId: number,
  staffingCommittee: MembersFieldDto[]
): Promise<AxiosResponse<void>> => {
  return await api.put(`/projects/${projectId}/staffing-committee`, staffingCommittee);
};

/**
 * Saves the team composition for the project
 * @param projectId The ID of the project
 * @param teamComposition The team composition to save
 * @returns A promise that resolves when the team composition is saved
 */
export const saveTeamComposition = async (
  projectId: number,
  teamComposition: ProjectTeamCompositionDto
): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/team-composition`, teamComposition);
};

export const startSecondApplicationPhase = async (
  projectId: number,
  secondApplicationPhase: SecondApplicationPhaseDto
): Promise<AxiosResponse<void>> => {
  return await api.post(`/projects/${projectId}/start-second-application-phase`, secondApplicationPhase);
};
